import { useQuery } from '@tanstack/react-query';
import api from '../../../Services/api';
import Globals from '../../../Globals.json';
import { summarizeTemporary, TemporarySale, TemporaryTotals } from './summarizeTemporary';

export type { TemporaryGroup, TemporaryTotals } from './summarizeTemporary';

/**
 * Total das vendas temporárias em aberto, separado em Mesas/Balcão e Delivery
 * (regra em ../saleOrigin; agrupamento em ./summarizeTemporary).
 *
 * O card "Vendas Temporárias" é a soma do que está em aberto naquele momento,
 * não a primeira página da listagem — por isso percorremos todas as páginas do
 * endpoint antes de somar.
 *
 * Não há filtro de data aqui de propósito: "em aberto agora" independe do
 * período selecionado nos filtros da dashboard.
 */

const MAX_PAGES = 20;

async function fetchTemporaryTotal(storeId: number | string) {
  let page = 1;
  let lastPage = 1;
  const rows: TemporarySale[] = [];

  do {
    const { data } = await api.get(Globals.api.salesTemporary, {
      params: { page, paginate: true, store_id: storeId },
    });

    rows.push(...((data?.data ?? []) as TemporarySale[]));
    lastPage = data?.meta?.last_page ?? 1;
    page += 1;
  } while (page <= lastPage && page <= MAX_PAGES);

  return summarizeTemporary(rows);
}

const EMPTY: TemporaryTotals = {
  total: 0,
  count: 0,
  tables: { total: 0, count: 0 },
  delivery: { total: 0, count: 0 },
};

export function useSalesTemporaryTotal(storeId?: number | string) {
  const query = useQuery({
    queryKey: ['sales-temporary', 'dashboard-total', storeId],
    queryFn: () => fetchTemporaryTotal(storeId as number | string),
    enabled: !!storeId,
    keepPreviousData: true,
  } as any);

  const data = (query.data as TemporaryTotals | undefined) ?? EMPTY;

  return {
    ...data,
    isLoading: query.isLoading && !!storeId,
    isFetching: query.isFetching,
  };
}
